import type { BadgeVariant } from '@/components/ui/Badge';

export type AsesoriaEstado = 'EN_CURSO' | 'EN_REVISION' | 'FINALIZADA' | 'REQUIERE_CORRECCIONES' | 'REVERTIDA' | 'SIN_EVALUAR';
export type AsesoriaEstadoLegacy = 'EN_CURSO' | 'PENDIENTE_VERIFICACION' | 'APROBADA';

const ACTIVE_STATES: AsesoriaEstado[] = ['EN_CURSO', 'EN_REVISION', 'REQUIERE_CORRECCIONES', 'REVERTIDA', 'SIN_EVALUAR'];

interface NormalizeAsesoriaEstadoInput {
  estado?: string | null;
  estadoVerificacion?: string | null;
  // Preaprobación transparente: una asesoría EN_REVISION marcada autoAprobar=true ya está
  // resuelta de cara al asesor/admin — debe verse exactamente igual que Finalizada, nunca como
  // "En revisión". Mismo criterio que estadoParaMostrar (AsesoriaHistorialTable) y
  // computeResumenHoras (HorasResumenCard).
  autoAprobar?: boolean;
}

export function normalizeAsesoriaEstado({ estado, estadoVerificacion, autoAprobar }: NormalizeAsesoriaEstadoInput): AsesoriaEstado {
  // SIN_EVALUAR cuenta igual que EN_REVISION: es su equivalente funcional (asesoría cuyo asesor
  // fue reasignado), así que una marcada autoAprobar también está resuelta.
  if ((estado === 'EN_REVISION' || estado === 'SIN_EVALUAR') && autoAprobar) {
    return 'FINALIZADA';
  }

  if (
    estado === 'EN_CURSO' ||
    estado === 'EN_REVISION' ||
    estado === 'FINALIZADA' ||
    estado === 'REQUIERE_CORRECCIONES' ||
    estado === 'REVERTIDA' ||
    estado === 'SIN_EVALUAR'
  ) {
    return estado;
  }

  if (estado === 'PENDIENTE_VERIFICACION') return 'EN_REVISION';
  if (estado === 'APROBADA') return 'FINALIZADA';

  if (estadoVerificacion === 'PENDIENTE_VERIFICACION') return 'EN_REVISION';
  if (estadoVerificacion === 'APROBADA') return 'FINALIZADA';

  return 'EN_CURSO';
}

export function getAsesoriaEstadoLabel(estado: AsesoriaEstado): string {
  switch (estado) {
    case 'EN_CURSO':
      return 'En curso';
    case 'EN_REVISION':
      return 'En revisión';
    case 'FINALIZADA':
      return 'Finalizada';
    case 'REQUIERE_CORRECCIONES':
      return 'Requiere correcciones';
    case 'REVERTIDA':
      return 'Revertida';
    case 'SIN_EVALUAR':
      return 'Sin evaluar';
  }
}

export function getAsesoriaEstadoBadgeVariant(estado: AsesoriaEstado): BadgeVariant {
  switch (estado) {
    case 'EN_CURSO':
      return 'blue';
    case 'EN_REVISION':
      return 'yellow';
    case 'FINALIZADA':
      return 'green';
    case 'REQUIERE_CORRECCIONES':
      return 'red';
    case 'REVERTIDA':
      return 'gray';
    case 'SIN_EVALUAR':
      return 'purple';
  }
}

export function isAsesoriaEditable(estado: AsesoriaEstado): boolean {
  return estado === 'EN_CURSO' || estado === 'REQUIERE_CORRECCIONES' || estado === 'REVERTIDA';
}

export function isAsesoriaActive(estado: AsesoriaEstado): boolean {
  return ACTIVE_STATES.includes(estado);
}

export const ASESORIA_READONLY_MESSAGE: Record<AsesoriaEstado, string> = {
  EN_CURSO: '',
  REQUIERE_CORRECCIONES: '',
  EN_REVISION: 'Esta asesoría está en revisión y no puede modificarse.',
  FINALIZADA: 'Esta asesoría está finalizada y no puede modificarse.',
  REVERTIDA: '',
  SIN_EVALUAR: 'Esta asesoría quedó sin evaluar por un cambio de asesor y no puede modificarse.',
};
